/**
 * Строки вкладок «Голоса клиента» (#848) — без React, чтобы порядок, доли и
 * выгрузка проверялись отдельно от разметки. Экран, CSV и печатная форма
 * строятся из ОДНИХ и тех же строк: второго источника правды у выгрузки нет.
 *
 * Какой срез читает какая вкладка — решение экрана, а не сервера: вкладки
 * названы по адресатам решения, не по полям схемы (спека §2.1).
 */
import {
  VOICE_SMALL_SAMPLE_CALLS,
  type ObjectionStreamCode,
  type VoiceCompetitor,
  type VoiceFeedbackSeverity,
  type VoiceMark as VoiceMarkField,
  type VoiceOfCustomerResponse,
  type VoiceSignalType,
} from '@aica/api-types/schemas';
import {
  declineReasonLabelFamily,
  isVoiceSmallSample,
  VOICE_NO_MARK,
  type VoiceMark,
} from '@aica/core/voc';
import { percent } from '@/components/analytics/objections/objection-view-model';
import { localizeObjectionCode } from '@/components/dashboards/objectionCodeNames';
import { localDayKey } from '@/lib/local-day';
import type { VoiceThemeExtra } from './voice-themes-view-model';
import type { VoiceAudience } from './voice-search';

export const MARKETING_SIGNALS: readonly VoiceSignalType[] = [
  'pricing',
  'demand',
  'trend',
];
export const PRODUCT_SIGNALS: readonly VoiceSignalType[] = ['supply', 'regulation'];

export type VoiceRowKind =
  | 'competitor'
  | 'feedback'
  | 'signal'
  | 'needs'
  // Вопросы клиентов и причины отказа (#843, #844) — `voice-questions-view-model.ts`.
  | 'question'
  | 'unanswered'
  | 'firstMinute'
  | 'decline'
  // Темы разговоров (#845) — `voice-themes-view-model.ts`.
  | 'theme'
  | 'noTheme'
  // Коды потока возражений в виде «Что изменилось» (#2552) — `objection-view-model.ts`.
  | 'objection';

/**
 * `YYYY-MM-DD` in the viewer's time zone. The instant's UTC date is the wrong
 * day east of UTC: a window starting at local midnight would name the export
 * after the previous day.
 */
export function localDateKey(iso: string): string {
  return localDayKey(new Date(iso));
}

export interface VoiceRowItem {
  recordId: string;
  createdAt: string;
  text: string;
  /** Продукт отзыва / тональность упоминания — пояснение под формулировкой. */
  detail: string | null;
}

export interface VoiceRow {
  /** `kind:key` — ключ раскрытия в адресе. */
  id: string;
  kind: VoiceRowKind;
  /** Уровень, тип сигнала или имя конкурента как есть. */
  key: string;
  /**
   * Ключ ряда строки (`resolved_key` фактов, #2394 §9 п. 7), когда он не
   * `key`: сведённый ключ конкурента, код темы. Нет — ряд по `key` или ряда нет
   * (`rowSeriesRef`).
   */
  seriesKey?: string;
  calls: number;
  prevCalls: number;
  /**
   * N of this row — the conversations whose analysis collected its block:
   * business intelligence for competitors, feedback and signals, the
   * qualification for needs. Shares and the change read it, not a global N.
   */
  callsInWindow: number;
  prevCallsInWindow: number;
  /**
   * The response has no previous numbers for the row — «without an answer»,
   * an objection code the stream could not compare, a theme whose base began
   * inside the window, «no theme» after the set changed: `prevCalls` holds 0
   * for the screen, the table leaves the cells empty.
   */
  noPrevious?: true;
  /**
   * Пометка строки по правилу Р3 (#2394 §9 п. 6) — её считает сервер: у него
   * прошлые периоды, срок хранения и покрытие фактами. Нет поля — нет пометки.
   */
  mark?: VoiceMarkField;
  items: VoiceRowItem[];
  /**
   * i18n key of the «in K of N …» line when it is not the kind's own: the
   * first minute names «calls and chats» only when the response says it
   * counts chats (`firstMinuteTextKeys`).
   */
  countsKey?: string;
  competitor?: Pick<VoiceCompetitor, 'merged' | 'sentiment'>;
  /** Тема разговоров: определение, категории, качество, отказы, вопросы без ответа. */
  theme?: VoiceThemeExtra;
  /**
   * The objection code of an objection row (`objectionSection`): its details
   * open like the code in the Objections section, with its stage, outcome and
   * best answers — not as a list of conversations.
   */
  objection?: ObjectionStreamCode;
}

/** Блоки вопросов клиентов и причин отказа (#843, #844): у каждого — своё N. */
export type QuestionSectionId = 'questions' | 'unanswered' | 'firstMinute' | 'declines';

/** Строки с категорией разговора вместо формулировки: темы (#845). */
export function isThemeKind(kind: VoiceRowKind): boolean {
  return kind === 'theme' || kind === 'noTheme';
}

export interface VoiceSection {
  /** i18n-ключ заголовка блока (`voicePage.sections.*`). */
  id:
    | 'competitors'
    | 'signals'
    | 'needs'
    | 'feedback'
    | 'themes'
    | 'objections'
    | QuestionSectionId;
  rows: VoiceRow[];
  /**
   * N of the block when it is not the response's: the questions blocks count
   * over the conversations where the step ran, declines over the declines.
   */
  callsInWindow?: number;
  /**
   * What a block with N = 0 says, when it is not the analysis profile's "not
   * collected": the questions blocks tell a step that has not run from a
   * window with no declines or no calls (`voice-questions-view-model.ts`).
   */
  zeroKey?: string;
  /**
   * What a collected block with no row says, when it is not the block's own
   * `voicePage.empty.*`: «without an answer» with no question at all is «no
   * questions», never «every question got a full answer».
   */
  emptyKey?: string;
  /**
   * What N counts, for the table's own column — a file read without the
   * screen's notes. By default the conversations that collect the block
   * (`voicePage.export.base.*`).
   */
  baseKey?: string;
  /**
   * What the block's own N counts, when its rows carry another one: the
   * summary line of an empty block shows the block's N, not a row's.
   */
  blockBaseKey?: string;
  /**
   * The block's own scan cap, when an export holds blocks of several requests
   * (Sales: the objection stream and the questions, #2350): each block says
   * its own, in the words its screen uses. No field — the export's
   * `truncated` / `prevTruncated` speak for the block.
   */
  cap?: VoiceSectionCap;
}

export interface VoiceSectionCap {
  truncated: boolean;
  prevTruncated: boolean;
  /** i18n keys of the two caveats — the screen's own words for the block. */
  truncatedKey: string;
  prevTruncatedKey: string;
}

/**
 * The caveats of a block with its own cap — one line per cap, the previous
 * window's only while it is comparable. The table's completeness column and
 * the printed card read the same lines.
 */
export function sectionCapNotes(
  section: Pick<VoiceSection, 'cap'>,
  comparable: boolean,
  t: Translate
): string[] {
  const { cap } = section;
  if (!cap) return [];
  return [
    cap.truncated ? t(cap.truncatedKey) : '',
    comparable && cap.prevTruncated ? t(cap.prevTruncatedKey) : '',
  ].filter(Boolean);
}

/** What a block's N counts — the table's «which conversations make N» column. */
function sectionBaseKey(section: Pick<VoiceSection, 'id' | 'baseKey'>): string {
  if (section.baseKey) return section.baseKey;
  return section.id === 'needs'
    ? 'voicePage.export.base.needs'
    : 'voicePage.export.base.calls';
}

function competitorRows(data: VoiceOfCustomerResponse): VoiceRow[] {
  return data.competitors.rows.map((row) => ({
    id: `competitor:${row.name}`,
    kind: 'competitor',
    key: row.name,
    ...(row.key ? { seriesKey: row.key } : {}),
    calls: row.calls,
    prevCalls: row.prevCalls,
    ...(row.mark ? { mark: row.mark } : {}),
    callsInWindow: data.callsInWindow,
    prevCallsInWindow: data.prevCallsInWindow,
    items: row.items.map((item) => ({
      recordId: item.recordId,
      createdAt: item.createdAt,
      text: item.text,
      detail: item.sentiment,
    })),
    competitor: { merged: row.merged, sentiment: row.sentiment },
  }));
}

function signalRows(
  data: VoiceOfCustomerResponse,
  types: readonly VoiceSignalType[]
): VoiceRow[] {
  return types.flatMap((type) => {
    const row = data.signals.find((signal) => signal.type === type);
    return row
      ? [
          {
            id: `signal:${type}`,
            kind: 'signal' as const,
            key: type,
            calls: row.calls,
            prevCalls: row.prevCalls,
            ...(row.mark ? { mark: row.mark } : {}),
            callsInWindow: data.callsInWindow,
            prevCallsInWindow: data.prevCallsInWindow,
            items: row.items.map((item) => ({ ...item, detail: null })),
          },
        ]
      : [];
  });
}

function feedbackRows(data: VoiceOfCustomerResponse): VoiceRow[] {
  return data.feedback.map((row) => ({
    id: `feedback:${row.severity}`,
    kind: 'feedback',
    key: row.severity,
    calls: row.calls,
    prevCalls: row.prevCalls,
    ...(row.mark ? { mark: row.mark } : {}),
    callsInWindow: data.callsInWindow,
    prevCallsInWindow: data.prevCallsInWindow,
    items: row.items.map((item) => ({
      recordId: item.recordId,
      createdAt: item.createdAt,
      text: item.text,
      detail: item.product,
    })),
  }));
}

function needsRow(data: VoiceOfCustomerResponse): VoiceRow {
  return {
    id: 'needs:needs',
    kind: 'needs',
    key: 'needs',
    calls: data.needs.calls,
    prevCalls: data.needs.prevCalls,
    ...(data.needs.mark ? { mark: data.needs.mark } : {}),
    callsInWindow: data.needs.callsInWindow,
    prevCallsInWindow: data.needs.prevCallsInWindow,
    items: data.needs.items.map((item) => ({ ...item, detail: null })),
  };
}

/**
 * Блоки вкладки. «Продажи» — экран «Возражения», «Сайт и контент» — ждёт
 * вопросов клиентов (#843) и тем (#845): у обеих здесь блоков нет.
 */
export function voiceSections(
  data: VoiceOfCustomerResponse,
  audience: VoiceAudience
): VoiceSection[] {
  if (audience === 'marketing') {
    return [
      { id: 'competitors', rows: competitorRows(data) },
      { id: 'signals', rows: signalRows(data, MARKETING_SIGNALS) },
      { id: 'needs', rows: [needsRow(data)] },
    ];
  }
  if (audience === 'product') {
    return [
      { id: 'feedback', rows: feedbackRows(data) },
      { id: 'signals', rows: signalRows(data, PRODUCT_SIGNALS) },
    ];
  }
  return [];
}

/** What every export reads of a response: the window and whether its blocks hit the cap. */
type VoiceExportWindow = Pick<
  VoiceOfCustomerResponse,
  'periodStart' | 'periodEnd' | 'truncated' | 'prevTruncated'
>;

/**
 * An export of the section's own tabs (Marketing, Product and service): the
 * section's response — its N, the needs' N and the glossary state are
 * required, so a block can never print «in K of 0» for want of them.
 */
export type VoiceSectionExportData = VoiceExportWindow &
  Pick<
    VoiceOfCustomerResponse,
    'callsInWindow' | 'needs' | 'competitors' | 'countsFrom'
  > & {
    kind?: never;
  };

/**
 * An export of the Sales and Site and content tabs: their blocks come from
 * the questions request (`only=customerQuestions`) and, on Sales, the
 * objection stream — each carries its own N.
 */
export type VoiceQuestionsExportData = VoiceExportWindow & { kind: 'questions' };

export type VoiceExportData = VoiceSectionExportData | VoiceQuestionsExportData;

/** What a block's N is read from — one of the two exports, or the section's response. */
type VoiceExportCounts =
  | Pick<VoiceSectionExportData, 'kind' | 'callsInWindow' | 'needs'>
  | Pick<VoiceQuestionsExportData, 'kind'>;

/** N of a block, for its header: the needs are counted over their own population. */
export function sectionCallsInWindow(
  section: Pick<VoiceSection, 'id' | 'callsInWindow'>,
  data: VoiceExportCounts
): number {
  // The questions blocks carry their own N.
  if (data.kind === 'questions') return section.callsInWindow ?? 0;
  if (section.callsInWindow !== undefined) return section.callsInWindow;
  return section.id === 'needs' ? data.needs.callsInWindow : data.callsInWindow;
}

/**
 * Why a block with N = 0 has nothing to count — the same words on the screen,
 * in the table and in print. By default the analysis profile does not collect
 * the block; the questions blocks name their own reason (`zeroKey`).
 */
export function sectionZeroKey(section: Pick<VoiceSection, 'zeroKey'>): string {
  return section.zeroKey ?? 'voicePage.notCollected';
}

/** What a block with N > 0 and no row says — the same on screen and in print. */
export function sectionEmptyKey(section: Pick<VoiceSection, 'id' | 'emptyKey'>): string {
  return section.emptyKey ?? `voicePage.empty.${section.id}`;
}

/**
 * Пометка строки — «выросло», «упало», «новое», «исчезло» (правило Р3,
 * `voiceMark` в `@aica/core/voc`): та же, что в печати и в ежемесячном
 * письме. Экран её не выводит из чисел строки: ответ без пометки (старый
 * сервер, неполная история) — пометки нет.
 */
export function rowMark(row: Pick<VoiceRow, 'mark'>): VoiceMark {
  return row.mark ?? VOICE_NO_MARK;
}

/** Строк за этим числом разговоров меньше порога — «догадка, а не закономерность». */
export function isSmallSample(calls: number): boolean {
  return isVoiceSmallSample(calls, VOICE_SMALL_SAMPLE_CALLS);
}

export { VOICE_NO_MARK, type VoiceMark } from '@aica/core/voc';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Пометка строки словами — печать и подписи; на экране та же пометка бейджем. */
export function rowMarkText(mark: VoiceMark, t: Translate): string {
  switch (mark.kind) {
    case 'new':
      return t('voicePage.trend.new');
    case 'gone':
      return t('voicePage.trend.gone');
    case 'up':
    case 'down':
      return t(`voicePage.trend.${mark.kind}`, { delta: Math.abs(mark.delta) });
    default:
      return '';
  }
}

/** Название строки на языке смотрящего; имя конкурента — как его свели. */
export function rowLabel(row: VoiceRow, t: Translate): string {
  switch (row.kind) {
    case 'competitor':
      return row.key;
    case 'feedback':
      return t(`voicePage.feedback.${row.key as VoiceFeedbackSeverity}`);
    case 'signal':
      return t(`voicePage.signal.${row.key as VoiceSignalType}`);
    case 'needs':
      return t('voicePage.needs.title');
    case 'question':
    case 'unanswered':
    case 'firstMinute':
      return t(`voicePage.questions.subject.${row.key}`);
    case 'decline':
      return declineReasonLabel(row.key, t);
    case 'theme':
      // The organization's own name for the theme, as it wrote it.
      return row.key;
    case 'noTheme':
      return t('voicePage.themes.analytics.noTheme');
    case 'objection':
      // As the card names it: a code newer than this bundle, or one whose
      // atom the bundle lacks, by its live wording — never a raw key.
      return localizeObjectionCode(row.key, row.objection?.wordings[0] ?? row.key, t);
  }
}

/**
 * Подпись причины отказа: семейство — по `declineReasonLabelFamily`
 * (`@aica/core/voc`, то же правило у письма): причины, которых нет у
 * возражений, — свои подписи; общие с возражениями коды — ТЕ ЖЕ подписи
 * `objectionCodes.*`: «цена» отказа и «цена» возражения — один ряд (#847).
 */
export function declineReasonLabel(code: string, t: Translate): string {
  return declineReasonLabelFamily(code) === 'decline'
    ? t(`voicePage.declines.reason.${code}`)
    : t(`objectionCodes.${code}`);
}

/**
 * «В K из N …» строки — по её знаменателю: разговоры для частоты вопросов,
 * свой ключ строки (`countsKey`) для первой минуты — звонки или звонки и чаты,
 * разговоры с этим вопросом для «без ответа», отказы для причин. Склонение —
 * по N.
 */
export function rowCountsText(row: VoiceRow, t: Translate): string {
  const options = {
    calls: row.calls,
    count: row.callsInWindow,
    share: percent(row.calls, row.callsInWindow),
  };
  if (row.countsKey) return t(row.countsKey, options);
  switch (row.kind) {
    case 'unanswered':
      return t('voicePage.questions.unansweredCalls', options);
    case 'decline':
      return t('voicePage.declines.calls', options);
    case 'objection':
      return t('voicePage.changes.objectionCalls', options);
    default:
      return t('voicePage.calls', options);
  }
}

/**
 * Формулировка для показа. Пустой `text` у вопроса или причины — её изъяла
 * редакция (спека §7): вопрос был, поэтому «формулировка скрыта», а не пусто.
 */
export function itemText(item: Pick<VoiceRowItem, 'text'>, t: Translate): string {
  return item.text === '' ? t('voicePage.questions.wordingHidden') : item.text;
}

/**
 * Оговорка ответа, чей проход упёрся в потолок (#2596): посчитанные по фактам
 * строки покрывают всё окно, и последним разговорам принадлежат только числа
 * самого прохода — тон о конкурентах и формулировки; посчитанные проходом —
 * все числа нижняя граница. Ответ вопросов пути фактов не имеет. `''` — ответ
 * целый.
 */
export function truncationNote(
  data: { truncated: boolean; countsFrom?: VoiceOfCustomerResponse['countsFrom'] },
  sections: readonly Pick<VoiceSection, 'id'>[],
  t: Translate
): string {
  if (!data.truncated) return '';
  if (data.countsFrom !== 'facts') return t('voicePage.truncated');
  return t(
    sections.some((section) => section.id === 'competitors')
      ? 'voicePage.truncatedFacts.marketing'
      : 'voicePage.truncatedFacts.product'
  );
}

/** Числа строк — нижняя граница: их посчитал проход, упёршийся в потолок. */
export function countsLowerBound(
  data: Pick<VoiceOfCustomerResponse, 'truncated' | 'countsFrom'>
): boolean {
  return data.truncated && data.countsFrom === 'scan';
}

/**
 * Таблица вкладки: одна строка на строку экрана, с теми же числами и первой
 * формулировкой. В выгружаемых блоках имён сотрудников нет: они есть только в
 * разбивках отказов «Продаж» (`VoiceSalesDeclines`) и во взгляде «Команда»
 * возражений, а те не выгружаются (`salesQuestionSections`, `objectionSection`).
 */
export function tableRows(
  sections: readonly VoiceSection[],
  data: Pick<VoiceOfCustomerResponse, 'truncated' | 'prevTruncated'> &
    (
      | Pick<
          VoiceSectionExportData,
          'kind' | 'callsInWindow' | 'needs' | 'competitors' | 'countsFrom'
        >
      | Pick<VoiceQuestionsExportData, 'kind'>
    ),
  t: Translate,
  options: { comparable?: boolean } = {}
): Record<string, string | number>[] {
  // Under a theme filter without a comparable previous window (§3) the file
  // carries no previous numbers at all: not their columns, not their caveat.
  const comparable = options.comparable ?? true;
  // A partial count leaves the screen with its caveat, so it leaves in the
  // file with it too: a table read without the screen must not look complete.
  const completeness = [
    truncationNote(
      {
        truncated: data.truncated,
        countsFrom: data.kind === 'questions' ? undefined : data.countsFrom,
      },
      sections,
      t
    ),
    comparable && data.prevTruncated ? t('voicePage.prevTruncated') : '',
  ]
    .filter(Boolean)
    .join(' ');
  // A block read by its own request (the objection stream) carries its own cap.
  const completenessOf = (section: VoiceSection) =>
    section.cap ? sectionCapNotes(section, comparable, t).join(' ') : completeness;
  // Unmerged spellings split one competitor's count — only its rows say so.
  const unmerged =
    data.kind !== 'questions' && data.competitors.mergeUnavailable
      ? t('voicePage.competitors.mergeUnavailable')
      : '';
  /** A block with nothing to list says why in words — one line, no numbers. */
  const noteRow = (
    section: VoiceSection,
    item: string,
    callsInWindow: number,
    caveat = ''
  ) => ({
    section: t(`voicePage.sections.${section.id}`),
    item,
    calls: '',
    callsInWindow,
    base: t(section.blockBaseKey ?? sectionBaseKey(section)),
    sharePercent: '',
    ...(comparable ? { prevCalls: '', prevCallsInWindow: '' } : {}),
    smallSample: '',
    completeness: caveat,
    wording: '',
  });
  return sections.flatMap((section): Record<string, string | number>[] => {
    const callsInWindow = sectionCallsInWindow(section, data);
    // N = 0: the screen says why there is nothing to count; so does the file —
    // its zero rows would read as a measured zero (#1379).
    // A block with its own cap keeps it: «none» over part of the window.
    if (callsInWindow === 0) {
      return [
        noteRow(
          section,
          t(sectionZeroKey(section)),
          0,
          section.cap ? completenessOf(section) : ''
        ),
      ];
    }
    // Collected, nothing named (every question answered in full): the
    // screen's empty line, not a block missing from the file.
    if (section.rows.length === 0) {
      // A partial count keeps its caveat: «none» over part of the window.
      return [
        noteRow(
          section,
          t(sectionEmptyKey(section)),
          callsInWindow,
          completenessOf(section)
        ),
      ];
    }
    const sectionCompleteness = completenessOf(section);
    return section.rows.map((row) => ({
      section: t(`voicePage.sections.${section.id}`),
      item: rowLabel(row, t),
      calls: row.calls,
      callsInWindow: row.callsInWindow,
      // N differs per block (declines, conversations with the question…):
      // the file names it, or one column reads as one population.
      base: t(sectionBaseKey(section)),
      // A row with no conversation of its base (a theme newer than the
      // window) has no share: an empty cell, not a measured 0 %.
      sharePercent: row.callsInWindow > 0 ? percent(row.calls, row.callsInWindow) : '',
      ...(comparable
        ? // A row without previous numbers: empty cells, not a measured «0 of 0».
          row.noPrevious
          ? { prevCalls: '', prevCallsInWindow: '' }
          : { prevCalls: row.prevCalls, prevCallsInWindow: row.prevCallsInWindow }
        : {}),
      smallSample: isSmallSample(row.calls) ? t('voicePage.smallSampleShort') : '',
      completeness:
        row.kind === 'competitor' && unmerged
          ? [sectionCompleteness, unmerged].filter(Boolean).join(' ')
          : sectionCompleteness,
      // A wording the redaction removed is no wording to export.
      wording: row.items.find((item) => item.text !== '')?.text ?? '',
    }));
  });
}

/** Пояснение под формулировкой: продукт отзыва или тональность упоминания. */
export function itemDetail(
  row: Pick<VoiceRow, 'kind'>,
  item: VoiceRowItem,
  t: Translate
): string {
  if (!item.detail) return '';
  if (isThemeKind(row.kind)) return t(`records:records.callCategory.${item.detail}`);
  return row.kind === 'competitor'
    ? t(`voicePage.sentiment.${item.detail}`)
    : item.detail;
}

/**
 * Подборка формулировок клиентов вкладки — для рекламы и текстов. Одна и та же
 * формулировка одного разговора — один раз, в первом блоке, где она есть: вопрос
 * первой минуты — это и частый вопрос, и вторая строка была бы дублем.
 */
export function wordingRows(
  sections: readonly VoiceSection[],
  t: Translate
): Record<string, string>[] {
  const seen = new Set<string>();
  return sections.flatMap((section) =>
    section.rows.flatMap((row) =>
      // A wording the redaction removed has nothing to give the copywriter.
      row.items
        .filter((item) => {
          if (item.text === '') return false;
          const key = JSON.stringify([item.recordId, item.text]);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((item) => ({
          section: t(`voicePage.sections.${section.id}`),
          item: rowLabel(row, t),
          wording: item.text,
          detail: itemDetail(row, item, t),
          date: localDateKey(item.createdAt),
          recordId: item.recordId,
        }))
    )
  );
}
